import { memo, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { inkOn } from '@core/palette';
import { dateRange, longDate, weeksOf } from '@core/dates';
import { Tooltip, type TipLine } from '@core/ui/Tooltip';
import { GRAY, assigneeBackground } from '@core/people';
import { cn } from '@core/ui/cn';
import { Menu, type MenuItem } from '@core/ui/Menu';
import type { Attention, Task } from './data';
import { BAR_HEIGHT, barTitleFit, type BarBox, type DragKind } from './geometry';
import { useTitleMeasurer } from './textWidth';
import type { SpotRange } from './spotlight';

export type { DragKind };
const DANGER_OUTLINE = '2px dashed #dc2626'; // красный пунктир: конец уйдёт за дату выхода
const SPOTLIGHT_RING = '0 0 0 2px #fff, 0 0 0 5px #f59e0b'; // янтарное кольцо подсвеченной задачи

/** Метки на всех барах экрана: цвета и имена исполнителей, чужое внимание, ожидающие записи, вопросы, выделение. */
export type BarMarks = {
  colors: Map<string, string[]>; names: Map<string, string[]>;
  attention: Map<string, Attention>;
  pendingIds: Set<string>; questions: Map<string, number>; selected: ReadonlySet<string>;
};
/** Как рисовать строку: высота бара (по строке), перенос названий (только у высоких баров), метки, дневная сетка, праздники, подсветка. */
export type BarLook = { barH: number; wrap: boolean; badges: boolean; releaseMarks: boolean; days: boolean; holidays: number[]; spot: SpotRange | null; spotBox: { left: number; width: number } | null };

/** Строки подсказки задачи — одни на бар, карточку релиза и чип: даты и длина, исполнители, метки, выход, дизайн. */
export function taskTipLines(t: Pick<Task, 'start_date' | 'end_date' | 'key_release' | 'release_confidence' | 'deploy' | 'deploy_date' | 'design_status'>, names: string[], questions = 0): TipLine[] {
  const weeks = weeksOf(t.start_date, t.end_date);
  return [
    `${dateRange(t.start_date, t.end_date)} · ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`,
    names.length ? names.join(', ') : { text: 'Unassigned', tone: 'muted' },
    ...(t.key_release ? [{ text: '★ Key release', tone: 'warn' as const }] : []),
    ...(t.release_confidence === 'at_risk' ? [{ text: '! At risk', tone: 'danger' as const }] : t.release_confidence === 'confirmed' ? [{ text: '✓ Confirmed', tone: 'ok' as const }] : []),
    ...(t.deploy === 'date' && t.deploy_date ? [`Deploy on ${longDate(t.deploy_date)}`] : t.deploy === 'none' ? [{ text: 'Not deploying', tone: 'muted' as const }] : []),
    ...(t.design_status === 'needed' ? [{ text: 'Design needed', tone: 'warn' as const }] : []),
    ...(questions ? [`${questions} open ${questions === 1 ? 'question' : 'questions'} — click ? to open`] : []),
  ];
}

/** Внешние метки: ключевой релиз, уверенность в дате. Белый знак на цветном квадратике — один вид на баре, карточке и чипе. */
export const releaseGlyphs = (t: Pick<Task, 'key_release' | 'release_confidence'>): { glyph: string; className: string; title: string }[] => [
  ...(t.key_release ? [{ glyph: '★', className: 'bg-amber-400', title: 'Key release' }] : []),
  ...(t.release_confidence === 'at_risk' ? [{ glyph: '!', className: 'bg-red-600', title: 'At risk' }]
    : t.release_confidence === 'confirmed' ? [{ glyph: '✓', className: 'bg-green-600', title: 'Confirmed' }] : []),
];

export function ReleaseMarks({ task, size = 'w-4 h-4 text-[11px]' }: { task: Pick<Task, 'key_release' | 'release_confidence'>; size?: string }) {
  const glyphs = releaseGlyphs(task);
  if (glyphs.length === 0) return null;
  return (
    <span data-release-marks className="inline-flex items-center gap-0.5 mr-1 align-middle">
      {glyphs.map((g) => <span key={g.glyph} className={cn('inline-flex items-center justify-center rounded-sm text-white font-bold leading-none', size, g.className)} aria-label={g.title}>{g.glyph}</span>)}
    </span>
  );
}

/** Кольцо выделения: белый зазор и акцент — отличимо от фокуса и чужой рамки. */
export const SELECTION_RING = '0 0 0 2px #fff, 0 0 0 5px var(--color-accent)';

/** Имя человека над баром, чьё внимание на задаче (первая из его задач). */
export function AttentionLabel({ attention }: { attention: Attention | undefined }) {
  if (!attention?.label) return null;
  return <span data-attention-label className="absolute -top-6 left-0 text-xs font-bold px-1.5 py-0.5 rounded whitespace-nowrap" style={{ background: attention.color, color: inkOn(attention.color) }}>{attention.name}</span>;
}

/** Ручки растяжения по краям бара; у края, обрезанного сеткой, ручки нет. */
export function ResizeHandles({ box, onPointerDown }: { box: BarBox; onPointerDown: (kind: DragKind, e: PointerEvent<HTMLElement>) => void }) {
  return (
    <>
      {!box.clippedLeft && <span className="absolute left-0 top-0 h-full w-2 cursor-ew-resize" onPointerDown={(e) => { e.stopPropagation(); onPointerDown('resize-start', e); }} />}
      {!box.clippedRight && <span className="absolute right-0 top-0 h-full w-2 cursor-ew-resize" onPointerDown={(e) => { e.stopPropagation(); onPointerDown('resize-end', e); }} />}
    </>
  );
}

/** Пунктирный контур: куда ляжет задача при броске или где появится новая. danger — конец уйдёт за дату выхода. */
export function GhostBar({ left, width, top, height, danger, children }: { left: number; width: number; top: number; height: number; danger?: boolean; children?: ReactNode }) {
  return (
    <div data-ghost data-danger={danger ? '' : undefined} className={cn('absolute rounded-md border-2 border-dashed z-[2] pointer-events-none flex items-center px-2 text-sm font-semibold', danger ? 'border-red-600 bg-red-100/70 text-red-700' : 'border-accent bg-accent/15 text-accent')} style={{ left, width, top, height }}>
      {children}
    </div>
  );
}

/** Ограничитель прибитой даты выхода у тащимой задачи: черта на её треке у конца недели выхода — дальше конец не уйдёт без вопроса. */
export function DeployFlag({ left, top, height, date }: { left: number; top: number; height: number; date: string }) {
  return (
    <span data-deploy-flag className="absolute z-[4] pointer-events-none" style={{ left, top: top - 4, height: height + 8 }}>
      <span className="absolute top-0 bottom-0 -left-px border-l-2 border-red-600" />
      <span className="absolute -top-5 -translate-x-full whitespace-nowrap rounded bg-white border border-red-400 text-red-700 text-[11px] leading-4 font-semibold px-1 shadow-sm">deploy {date} ⚑</span>
    </span>
  );
}

type Props = {
  task: Task;
  box: BarBox;
  top: number;
  colors: string[];
  names: string[];
  attention?: Attention | undefined; // у другого выделена, в руках или открыта: рамка его цвета, имя на одной из его задач
  pending: boolean;
  ghost?: boolean;          // его тащат: полупрозрачный, поверх остального
  selected?: boolean;       // в выделении: двойное кольцо, отличимое от фокуса и чужой правки
  spotlit?: boolean;        // подсвечен на всей сетке
  dim?: boolean;            // вне дат подсвеченного — гаснет
  danger?: boolean;         // тащат группой за дату выхода — красный контур
  dataIn?: string;          // строка, в которой бар (People: одна задача в нескольких строках)
  onPointerDown: (kind: DragKind, e: PointerEvent<HTMLElement>) => void;
  onOpen: () => void;
  onOpenQuestions?: () => void;
  onKey: (e: KeyboardEvent<HTMLElement>) => void;
  menu?: MenuItem[];        // правая кнопка
  questions?: number;       // есть открытые вопросы — метка «?»
  badges?: boolean;         // метки на правом краю (вопросы, дизайн) — личный флаг
  releaseMarks?: boolean;   // глифы ★ ! ✓ перед названием — личный флаг
  height?: number;          // 36, 48 или 60 — по самому длинному названию строки (geometry.barHeightFor)
  wrap?: boolean;           // полные названия: перенос на две строки 14px, при нехватке — три 12px
};

export const TaskBar = memo(function TaskBar({ task, box, top, colors, names, attention, pending, ghost, selected, dataIn, onPointerDown, onOpen, onOpenQuestions, onKey, menu, questions, badges = true, releaseMarks = true, height = BAR_HEIGHT, wrap = false, spotlit = false, dim = false, danger = false }: Props) {
  // Полные названия: сначала 14px в две строки; мельче (12px, три строки) — только если не влезло.
  const titleRef = useRef<HTMLSpanElement>(null);
  const [small, setSmall] = useState(false);
  useLayoutEffect(() => { setSmall(false); }, [task.title, box.width, wrap]);
  useLayoutEffect(() => {
    if (!wrap || small) return;
    const el = titleRef.current;
    // не влезло по высоте (третья строка) или по ширине (слово длиннее строки, на 14px слова не рвём)
    if (el && (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1)) setSmall(true);
  });
  // не влезло в одну строку — маска вместо «…»; на совсем узком баре остаются одни глифы
  const measure = useTitleMeasurer();
  const fit = wrap ? { clipped: false, titleHidden: false } : barTitleFit(task.title, box.width, releaseMarks ? releaseGlyphs(task).length : 0, measure);
  const outline = danger ? DANGER_OUTLINE : attention ? `2px solid ${attention.color}` : undefined;
  const bar = (
    <div
      role="button"
      tabIndex={0}
      data-task={task.id}
      data-in={dataIn}
      data-selected={selected ? '' : undefined}
      data-attention={attention ? attention.name : undefined}
      data-spotlit={spotlit ? '' : undefined}
      data-dim={dim ? '' : undefined}
      data-danger={danger ? '' : undefined}
      aria-label={`${task.title}, ${task.start_date} – ${task.end_date}${names.length ? ', ' + names.join(', ') : ''}`}
      className={cn(
        'min-h-0 absolute rounded font-bold px-2 flex select-none cursor-grab',
        wrap ? 'items-start py-1' : 'items-center',
        wrap && small ? 'text-xs' : 'text-sm',
        'shadow-sm overflow-visible focus:outline-none focus-visible:ring-4 focus:ring-4 ring-accent/70',
        pending && 'opacity-70',
        dim && 'opacity-30',
        ghost && 'opacity-50 z-[3] cursor-grabbing shadow-lg',
        box.clippedLeft && 'rounded-l-none',
        box.clippedRight && 'rounded-r-none',
      )}
      style={{
        left: box.left, width: box.width, top, height,
        background: assigneeBackground(colors), color: inkOn(...(colors.length ? colors : [GRAY])), outline, outlineOffset: 2,
        boxShadow: selected ? SELECTION_RING : spotlit ? SPOTLIGHT_RING : undefined,
      }}
      onPointerDown={(e) => onPointerDown('move', e)}
      onDoubleClick={onOpen}
      onKeyDown={onKey}
    >
      <span ref={titleRef} data-title-fit={fit.titleHidden ? 'glyphs' : fit.clipped ? 'clipped' : undefined} className={cn(wrap ? (small ? 'line-clamp-3 break-words leading-4' : 'line-clamp-2 break-normal overflow-hidden leading-5') : 'overflow-hidden whitespace-nowrap', fit.clipped && !fit.titleHidden && '[mask-image:linear-gradient(to_right,#000_calc(100%_-_16px),transparent)]')}>
        {releaseMarks && <ReleaseMarks task={task} />}{!fit.titleHidden && task.title}
      </span>
      {/* метки на правой границе, как в старом: не отъедают место у названия */}
      {badges && (!!questions || task.design_status === 'needed') && (
        <span className="absolute -top-2 -right-1 flex items-center gap-0.5 z-[2]">
          {!!questions && (
            <button
              type="button" aria-label={`${questions} open questions — open`}
              className="min-h-0 w-4 h-4 rounded-full bg-white text-gray-900 text-[11px] font-bold leading-none flex items-center justify-center shadow border border-gray-400"
              onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onOpenQuestions?.(); }}
            >?</button>
          )}
          {task.design_status === 'needed' && <span className="w-4 h-4 rounded-full bg-yellow-400 border border-white shadow" aria-label="Design needed" />}
        </span>
      )}

      <AttentionLabel attention={attention} />
      <ResizeHandles box={box} onPointerDown={onPointerDown} />
    </div>
  );
  // подсказка не показывается тому, кого тащат: контент под курсором и так меняется
  const tipped = <Tooltip disabled={ghost} title={task.title} lines={taskTipLines(task, names, questions)} hint="Double-click to open · drag to move">{bar}</Tooltip>;
  return menu ? <Menu items={menu}>{tipped}</Menu> : tipped;
});
